import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  titleId?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  contentClassName?: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export default function Modal({
  isOpen,
  onClose,
  title,
  titleId,
  children,
  footer,
  contentClassName = "",
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const resolvedTitleId: string =
    titleId ?? `form-modal-${slugify(title || "")}`;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleEscape(e: KeyboardEvent): void {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    }

    window.addEventListener("keydown", handleEscape);
    return (): void => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent background scroll (and reduce pull-to-refresh) while any modal is open
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const body: any = document.body;
    body.__modalOpenCount = (body.__modalOpenCount || 0) + 1;
    body.classList.add("modal-open");

    return () => {
      const currentBody: any = document.body;
      if (currentBody.__modalOpenCount) {
        currentBody.__modalOpenCount -= 1;
      }
      if (!currentBody.__modalOpenCount || currentBody.__modalOpenCount <= 0) {
        currentBody.__modalOpenCount = 0;
        currentBody.classList.remove("modal-open");
      }
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !modalRef.current) return;

    previousActiveElement.current =
      document.activeElement as HTMLElement | null;

    const focusable =
      modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusable[0];
    if (first) first.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const modal = modalRef.current;
      const elements = modal?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!elements?.length || !modal) return;

      const firstEl = elements[0];
      const lastEl = elements[elements.length - 1];
      const active = document.activeElement as HTMLElement;

      if (active && !modal.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? lastEl : firstEl).focus();
        return;
      }

      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  return createPortal(
    !isOpen ? null : (
      <div
        className="modal-overlay px-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby={resolvedTitleId}
      >
        <div
          ref={modalRef}
          className={`modal-content ${contentClassName}`}
          onClick={(e) => e.stopPropagation()}
          tabIndex={-1}
        >
          {title && (
            <div className="modal-header position-relative">
              <p
                id={resolvedTitleId}
                className="fs-5 fw-medium text-white m-0 text-center"
              >
                {title}
              </p>
              {onClose && (
                <button
                  type="button"
                  className="close-btn position-absolute top-50 translate-middle-y"
                  style={{ right: "0.75rem" }}
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X size={20} absoluteStrokeWidth={true} />
                </button>
              )}
            </div>
          )}
          <div className="modal-body">{children}</div>
          {!!footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    ),
    document.body,
  );
}
